import { Form, Row } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CompanySettingsCard from '../CompanySettingsCard';
import CompanyEditRow from '../CompanyEditRow';
import DraggableTable from '../../projects/ProjectScheduleOfValues/DraggableTable';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import Colors from '../../constants/Colors';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import { addScheduleCategory, deleteScheduleCategory, getScheduleCategories, reorderScheduleCategories, updateScheduleCategory } from '../../schedule/state/schedule.actions';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import useToggle from '../../common/hooks/useToggle';
import SimpleFormModal from '../../common/modals/SimpleFormModal';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import FormColorPicker from '../../common/inputs/FormColorPicker';

const HELP_TEXT = 'Create and manage categories to organize and color-code shifts on the schedule.';

const onHeaderCell = () => ({
  style: {
    backgroundColor: 'white',
  },
});

export default function ScheduleCategories() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { toggle, isToggled } = useToggle();

  const categories = useSelector((state) => state.schedule.categories);

  const [selectedEntry, setSelectedEntry] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(getScheduleCategories());
  }, []);

  useEffect(() => {
    if (!isToggled) {
      setSelectedEntry();
      return form.resetFields();
    }

    return form.setFieldsValue({
      ...(selectedEntry || {}),
      color: selectedEntry?.color || '#FFFFFF',
    });
  }, [isToggled, form, selectedEntry]);

  const onDeleteClicked = useCallback(async (record) => new Promise((resolve) => {
    CustomConfirmModal({
      title: `Delete Category ${record.name}?`,
      content: 'Are you sure you want to delete this category?',
      onOk: async () => {
        await dispatch(deleteScheduleCategory(record.id));
        resolve();
      },
      okText: 'Delete',
    });
  }, []));

  const closeModal = useCallback(() => {
    setSelectedEntry();
    toggle();
  }, [toggle]);

  const onSave = useCallback(async () => {
    const payload = form.getFieldsValue();
    const result = selectedEntry
      ? await dispatch(updateScheduleCategory(selectedEntry.id, payload))
      : await dispatch(addScheduleCategory(payload));

    if (result) closeModal();
  }, [form, closeModal, selectedEntry]);

  const onMove = useCallback(async (id, _, newIndex) => {
    // Index starts at 1
    setLoading(true);
    await dispatch(reorderScheduleCategories(id, { newIndex: newIndex + 1 }));
    setLoading(false);
  }, []);

  const columns = useMemo(() => (
    [
      {
        title: 'Color',
        dataIndex: 'color',
        key: 'color',
        width: 40,
        onHeaderCell,
        render: (color) => <FormColorPicker value={color} showText={false} style={{ marginRight: 35 }} />,
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        onHeaderCell,
      },
      {
        dataIndex: 'edit',
        align: 'edit',
        width: 60,
        onHeaderCell,
        render: (_, record) => {
          const { id } = record || {};
          return (
            <BorderlessButton
              key={id}
              title=""
              icon="edit"
              color={Colors.ONTRACCR_BLACK}
              onClick={() => {
                setSelectedEntry(record);
                toggle();
              }}
              style={{
                paddingRight: 8,
                paddingLeft: 0,
              }}
            />
          );
        },
      },
      {
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        width: 60,
        onHeaderCell,
        render: (_, record) => (
          <BorderlessButton
            title=""
            icon="delete"
            color={Colors.opacity(Colors.ONTRACCR_RED, 1)}
            onClick={() => {
              onDeleteClicked(record);
            }}
            style={{
              paddingRight: 8,
              paddingLeft: 0,
              cursor: 'pointer',
            }}
          />
        ),
      },
    ]
  ), [onDeleteClicked, toggle]);

  return (
    <Row justify="start">
      <CompanySettingsCard title="Schedule Categories">
        <CompanyEditRow
          title="Schedule Categories"
          helpText={HELP_TEXT}
          divider={false}
        >
          <OnTraccrButton
            title="Add"
            type="primary"
            onClick={toggle}
          />
        </CompanyEditRow>
        <DraggableTable
          columns={columns}
          dataSource={categories}
          pagination={false}
          size="small"
          onMove={onMove}
          isDraggable
          loading={loading}
        />
      </CompanySettingsCard>
      <SimpleFormModal
        title={`${selectedEntry ? 'Edit' : 'Add'} Category`}
        visible={isToggled}
        onSave={onSave}
        onClose={closeModal}
      >
        <div style={{ width: '100%', display: 'inline-flex' }}>
          <Form form={form} layout="inline">
            <Form.Item name="color">
              <FormColorPicker isNotDisplay showText={false} style={{ marginLeft: 35 }} />
            </Form.Item>
            <Form.Item
              name="name"
              label="Name"
              style={{ width: 'inherit', marginLeft: 35 }}
            >
              <OnTraccrTextInput placeholder="Enter Name" />
            </Form.Item>
          </Form>
        </div>
      </SimpleFormModal>
    </Row>
  );
}